import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';

import { SessionStatus } from '@core/models/brijjsports.enums';
import { Athlete, ScoringSession } from '@core/models/brijjsports.models';
import { ToastService } from '@core/services/toast.service';
import { BasketballScoringAdapter } from '@core/sports/basketball/basketball-scoring.adapter';
import {
  BasketballPlayerLine,
  BoxScoreLine,
  PLAY_KIND_LABEL,
  PlayKind,
  PlayTeam
} from '@core/sports/basketball/basketball.models';
import { GameClockService } from '@core/sports/basketball/game-clock.service';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';

interface PlayerRow {
  athleteId: string;
  name: string;
  initials: string;
  line: BasketballPlayerLine;
  totalPoints: number;
}

interface PlayLogRow {
  id: string;
  team: PlayTeam;
  quarter: number;
  clockSec: number;
  playerName: string;
  label: string;
  at: string;
}

const STAT_KEYS: (keyof Pick<
  BasketballPlayerLine,
  'points' | 'assists' | 'rebounds' | 'steals' | 'blocks' | 'fouls' | 'minutesPlayed'
>)[] = ['points', 'assists', 'rebounds', 'steals', 'blocks', 'fouls', 'minutesPlayed'];

interface QuickAction {
  kind: PlayKind;
  label: string;
  tone?: 'primary' | 'accent' | 'warn';
}

const QUICK_ACTIONS: QuickAction[] = [
  { kind: '2pt_made', label: '+2', tone: 'primary' },
  { kind: '3pt_made', label: '+3', tone: 'primary' },
  { kind: 'ft_made', label: 'FT' },
  { kind: '2pt_miss', label: 'Miss 2' },
  { kind: '3pt_miss', label: 'Miss 3' },
  { kind: 'ft_miss', label: 'Miss FT' },
  { kind: 'reb_off', label: 'O-Reb' },
  { kind: 'reb_def', label: 'D-Reb' },
  { kind: 'ast', label: 'AST' },
  { kind: 'stl', label: 'STL' },
  { kind: 'blk', label: 'BLK' },
  { kind: 'foul_personal', label: 'Foul', tone: 'warn' }
];

@Component({
  selector: 'app-basketball-live-scorer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatTabsModule
  ],
  templateUrl: './basketball-live-scorer.component.html',
  styleUrl: './basketball-live-scorer.component.scss'
})
export class BasketballLiveScorerComponent implements OnDestroy {
  /** Session id from route param. */
  readonly sessionId = input.required<string>();

  protected readonly state = inject(BrijjSportsStateService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly tenants = inject(TenantContextService);
  protected readonly adapter = inject(BasketballScoringAdapter);
  protected readonly clockSvc = inject(GameClockService);

  protected readonly displayedColumns = [
    'name',
    'points',
    'assists',
    'rebounds',
    'steals',
    'blocks',
    'fouls',
    'minutesPlayed',
    'total'
  ];
  protected readonly boxColumns = ['name', 'pts', 'fg', '3p', 'ft', 'reb', 'ast', 'stl', 'blk', 'pf'];
  protected readonly quickActions = QUICK_ACTIONS;
  protected readonly playKindLabel = PLAY_KIND_LABEL;
  protected readonly selectedPlayer = signal<string | null>(null);

  protected readonly session = computed<ScoringSession | undefined>(() => this.state.sessionById(this.sessionId()));
  protected readonly game = computed(() => this.state.basketballGameBySession(this.sessionId()));
  protected readonly clock = computed(() => this.game()?.clock ?? null);
  protected readonly homeScore = computed(() => {
    const g = this.game();
    return g ? this.adapter.teamScore(g, 'home') : 0;
  });
  protected readonly awayScore = computed(() => {
    const g = this.game();
    return g ? this.adapter.teamScore(g, 'away') : 0;
  });
  protected readonly clockDisplay = computed(() => formatClock(this.clock()?.remainingSec ?? 0));

  protected readonly quarters = computed(() => {
    const g = this.game();
    return g ? Array.from({ length: g.quarterCount }, (_, i) => i + 1) : [];
  });

  protected readonly players = computed<Athlete[]>(() => {
    const ids = this.session()?.participantIds ?? [];
    return ids.map(id => this.state.athleteById(id)).filter((a): a is Athlete => !!a);
  });

  protected readonly onCourt = computed<Athlete[]>(() => {
    const g = this.game();
    const ids = new Set(g?.onCourt ?? []);
    return this.players().filter(a => ids.has(a.id));
  });

  protected readonly bench = computed<Athlete[]>(() => {
    const g = this.game();
    const ids = new Set(g?.onCourt ?? []);
    return this.players().filter(a => !ids.has(a.id));
  });

  protected readonly playLog = computed<PlayLogRow[]>(() => {
    const plays = this.game()?.plays ?? [];
    return [...plays].reverse().map(p => ({
      id: p.id,
      team: p.team,
      quarter: p.quarter,
      clockSec: p.clockSec,
      playerName: this.state.athleteById(p.athleteId)?.name ?? (p.team === 'away' ? 'Away team' : '—'),
      label: PLAY_KIND_LABEL[p.kind],
      at: p.at
    }));
  });

  protected readonly boxScoreRows = computed<(BoxScoreLine & { name: string; initials: string })[]>(() => {
    const g = this.game();
    if (!g) return [];
    return this.adapter.boxScore(g).map(row => {
      const a = this.state.athleteById(row.athleteId);
      return { ...row, name: a?.name ?? '—', initials: a?.initials ?? '?' };
    });
  });

  constructor() {
    queueMicrotask(() => this.clockSvc.attach(this.sessionId()));
  }

  ngOnDestroy(): void {
    this.clockSvc.detach();
  }

  formatClockSec(sec: number): string {
    return formatClock(sec);
  }

  toggleClock(): void {
    if (this.clock()?.running) this.clockSvc.pause();
    else this.clockSvc.start();
  }

  nextQuarter(): void {
    this.clockSvc.advanceQuarter();
  }

  resetQuarter(): void {
    this.clockSvc.resetQuarter();
  }

  selectPlayer(id: string): void {
    this.selectedPlayer.set(this.selectedPlayer() === id ? null : id);
  }

  toggleSub(athleteId: string): void {
    this.state.toggleOnCourt(this.sessionId(), athleteId);
  }

  recordHome(kind: PlayKind, athleteId?: string): void {
    const aid = athleteId ?? this.selectedPlayer();
    if (!aid) {
      this.toast.error('Select a player first');
      return;
    }
    const c = this.clock();
    this.state.recordBasketballPlay(this.sessionId(), {
      athleteId: aid,
      team: 'home',
      quarter: c?.currentQuarter ?? 1,
      clockSec: c?.remainingSec ?? 0,
      kind
    });
  }

  recordAway(kind: PlayKind): void {
    const c = this.clock();
    this.state.recordBasketballPlay(this.sessionId(), {
      athleteId: 'away-team',
      team: 'away',
      quarter: c?.currentQuarter ?? 1,
      clockSec: c?.remainingSec ?? 0,
      kind
    });
  }

  undoLast(): void {
    this.state.undoLastBasketballPlay(this.sessionId());
  }

  shareLiveLink(): void {
    const slug = this.tenants.currentTenant().slug;
    const url = `${window.location.origin}/live/${slug}/${this.sessionId()}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => this.toast.success('Live link copied', url),
        () => this.toast.error('Could not copy link', url)
      );
    } else {
      this.toast.success('Live link', url);
    }
  }

  rowsForQuarter(quarter: number): PlayerRow[] {
    const sess = this.session();
    if (!sess) return [];
    return this.players().map(a => {
      const game = this.game();
      const existing = game?.lines.find(l => l.athleteId === a.id && l.quarter === quarter);
      const line: BasketballPlayerLine = existing ?? {
        id: '',
        athleteId: a.id,
        quarter,
        points: 0,
        assists: 0,
        rebounds: 0,
        steals: 0,
        blocks: 0,
        fouls: 0,
        minutesPlayed: 0
      };
      return {
        athleteId: a.id,
        name: a.name,
        initials: a.initials,
        line,
        totalPoints: this.adapter.athleteTotalInSession(sess, a.id)
      };
    });
  }

  updateStat(athleteId: string, quarter: number, key: string, ev: Event): void {
    const value = Math.max(0, Math.round(Number((ev.target as HTMLInputElement).value) || 0));
    const game = this.game();
    if (!game) return;
    const existing = game.lines.find(l => l.athleteId === athleteId && l.quarter === quarter);
    const base: BasketballPlayerLine = existing ?? {
      id: '',
      athleteId,
      quarter,
      points: 0,
      assists: 0,
      rebounds: 0,
      steals: 0,
      blocks: 0,
      fouls: 0,
      minutesPlayed: 0
    };
    this.state.recordBasketballLine(this.sessionId(), { ...base, [key]: value });
  }

  end(): void {
    this.clockSvc.pause();
    this.state.endSession(this.sessionId());
    this.toast.success('Game ended');
    this.router.navigate(['/coach/scoring']);
  }

  pause(): void {
    this.state.pauseSession(this.sessionId());
  }

  resume(): void {
    this.state.resumeSession(this.sessionId());
  }

  protected readonly statusActive = SessionStatus.Active;
  protected readonly statusPaused = SessionStatus.Paused;
  protected readonly statKeys = STAT_KEYS;

  quarterScore(quarter: number): number {
    return this.rowsForQuarter(quarter).reduce((s, r) => s + r.line.points, 0);
  }

  trackPlay(_: number, p: PlayLogRow): string {
    return p.id;
  }
  trackAthlete(_: number, a: Athlete): string {
    return a.id;
  }
  trackBox(_: number, r: { athleteId: string }): string {
    return r.athleteId;
  }
}

function formatClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
